import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { NgIf } from '@angular/common';

/** Valor que emitira el control al formulario padre (modo por defecto) */
export interface UsuarioAssetPayload {
  file: File;                 // archivo FINAL (reprocesado)
  dataUrl: string | null;     // ej. "data:image/jpeg;base64,AAA..."
  base64: string | null;      // base64 puro sin prefijo
  contentType: string;        // ej: image/jpeg
  bytesSize: number;
  widthPx: number;
  heightPx: number;
  hashSha256Base64: string;   // hash SHA-256 en base64
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FileUploadComponent),
    multi: true,
  }],
  imports: [MatIcon, MatButton, NgIf],
  standalone: true
})
export class FileUploadComponent implements OnInit, OnDestroy, ControlValueAccessor {
  /** Tipos aceptados para el input */
  @Input() acceptedTypes = 'image/*';

  /** Cuando es TRUE, el control **emite y acepta SOLO base64 (string)** en vez de UsuarioAssetPayload */
  @Input() emitBase64Only = false;

  /** Si viene una URL externa (modo edicion), se usa como preview inicial */
  private _externalSrc: string | null = null;
  @Input() set externalSrc(value: string | null) {
    this._externalSrc = value;
    if (value) {
      this.previewDataUrl = value;
      this.resultBytes = 1;
    } else {
      this.previewDataUrl = null;
      this.resultBytes = 0;
    }
  }
  get externalSrc(): string | null {
    return this._externalSrc;
  }

  /** Preview circular opcional */
  @Input() circle = false;

  /** Tamaño objetivo en KB (por defecto 20 KB) */
  @Input() targetKB = 20;

  /** Lado maximo inicial (0 = usar tamaño original).  */
  @Input() initialMaxSide = 0;

  /** Alias de compatibilidad: [initialMaxSize] usado en templates existentes */
  @Input() set initialMaxSize(v: number | string) {
    const n = typeof v === 'string' ? parseInt(v, 10) : v;
    this.initialMaxSide = Number.isFinite(n as number) ? (n as number) : 0;
  }

  /** Emite cada vez que cambia el archivo (o null si se limpia) — mantiene compatibilidad */
  @Output() changed = new EventEmitter<UsuarioAssetPayload | null>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  /** Estado interno */
  file: File | null = null;              // archivo FINAL (tras renderizar)
  previewDataUrl: string | null = null;  // preview del archivo FINAL
  resultBytes = 0;                       // tamaño en bytes del archivo FINAL

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};
  private subs = new Subscription();

  ngOnInit(): void {
    if (this.externalSrc) {
      this.previewDataUrl = this.externalSrc;
      this.resultBytes = 1; // para estilos de “hay imagen”
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // =========================
  // ControlValueAccessor
  // =========================
  writeValue(obj: any): void {
    // En modo base64-only, puede llegar string (base64) o payload (si lo alimentan asi)
    if (this.emitBase64Only) {
      const b64: string | null =
        typeof obj === 'string' ? obj : (obj?.base64 ?? null);

      if (!b64) {
        this.clearFile(false);
        return;
      }
      // Asumimos JPEG porque el render del componente exporta JPEG
      this.previewDataUrl = `data:image/jpeg;base64,${b64}`;
      this.resultBytes = 1;
      this.file = null; // no tenemos File al setear desde fuera
      return;
    }

    // Modo normal (payload)
    const payload = obj as UsuarioAssetPayload | null;
    if (!payload) {
      this.clearFile(false);
      return;
    }
    if (payload.dataUrl) {
      this.previewDataUrl = payload.dataUrl;
      this.resultBytes = payload.bytesSize || 1;
      this.file = payload.file ?? null;
    }
  }

  registerOnChange(fn: (val: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.fileInput) this.fileInput.nativeElement.disabled = isDisabled;
  }

  // =========================
  // Handlers
  // =========================
  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const original = input.files[0];
    await this.processFile(original);
  }

  /** Procesa el archivo: renderiza en canvas y reduce SOLO dimensiones hasta ≤ targetKB */
  private async processFile(original: File): Promise<void> {
    // 1) Cargar imagen
    const img = await this.loadHTMLImageFromFile(original);

    // 2) Renderizar y reducir dimensiones hasta target
    const targetBytes = this.targetKB * 1024;
    const { blob, width, height, dataURL } = await this.renderToTargetBytes(img, {
      mime: 'image/jpeg',           // exportamos SIEMPRE JPEG
      targetBytes,
      initialMaxSide: this.initialMaxSide || 0,
      reduceFactor: 0.85,           // baja dimensiones, NO quality
    });

    // 3) Crear File FINAL desde el blob resultante
    const finalName = this.ensureExtension(original.name, 'jpg');
    const finalFile = new File([blob], finalName, { type: 'image/jpeg' });

    // 4) Calcular hash del archivo FINAL
    const arrBuff = await blob.arrayBuffer();
    const hashBase64 = await this.sha256ToBase64(arrBuff);

    // 5) Armar payload (incluyendo base64 “puro”)
    const base64 = this.extractBase64(dataURL);

    const payload: UsuarioAssetPayload = {
      file: finalFile,
      dataUrl: dataURL,
      base64,
      contentType: finalFile.type, // "image/jpeg"
      bytesSize: finalFile.size,
      widthPx: width,
      heightPx: height,
      hashSha256Base64: hashBase64,
    };

    // 6) Actualizar estado/preview
    this.file = finalFile;
    this.previewDataUrl = dataURL;
    this.resultBytes = finalFile.size;

    // 7) Emitir segun modo
    if (this.emitBase64Only) {
      // → el control del form recibe SOLO base64 (string)
      this.changed.emit(payload); // compatibilidad para listeners existentes
      this.onChange(base64 ?? null);
    } else {
      // → el control del form recibe UsuarioAssetPayload
      this.changed.emit(payload);
      this.onChange(payload);
    }
    this.onTouched();
  }

  clearFile(emit: boolean = true): void {
    this.file = null;
    this.previewDataUrl = this.externalSrc || null;
    this.resultBytes = 0;

    if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
    if (emit) {
      this.changed.emit(null);
      this.onChange(this.emitBase64Only ? null : null);
    }
  }

  getKB(bytes: number): string {
    if (!bytes) return '0';
    return (bytes / 1024).toFixed(1);
  }

  // =========================
  // Helpers de imagen/canvas
  // =========================
  private loadHTMLImageFromFile(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });
  }

  private async renderToTargetBytes(
    img: HTMLImageElement,
    opts: {
      mime: 'image/jpeg';
      targetBytes: number;
      initialMaxSide: number; // 0 = sin tope inicial
      reduceFactor: number;   // p.ej 0.85
    }
  ): Promise<{ blob: Blob; width: number; height: number; dataURL: string }> {
    const { mime, targetBytes, initialMaxSide, reduceFactor } = opts;

    // dimensiones base (posible limitacion inicial por lado mas largo)
    let w = img.naturalWidth;
    let h = img.naturalHeight;

    if (initialMaxSide && (w > initialMaxSide || h > initialMaxSide)) {
      const scale = initialMaxSide / Math.max(w, h);
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('No fue posible obtener el contexto 2D del canvas');

    const drawAndExport = (): Promise<{ blob: Blob; dataURL: string }> =>
      new Promise((resolve, reject) => {
        canvas.width = w;
        canvas.height = h;
        if (!ctx) return reject(new Error('No ctx'));
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error('No se pudo generar el blob'));
          resolve({ blob, dataURL: canvas.toDataURL(mime) });
        }, mime);
      });

    let out = await drawAndExport();

    while (out.blob.size > targetBytes && (w > 32 && h > 32)) {
      w = Math.max(32, Math.floor(w * reduceFactor));
      h = Math.max(32, Math.floor(h * reduceFactor));
      out = await drawAndExport();
    }

    return { blob: out.blob, width: w, height: h, dataURL: out.dataURL };
  }

  private extractBase64(dataUrl: string | null): string | null {
    if (!dataUrl) return null;
    const i = dataUrl.indexOf('base64,');
    return i >= 0 ? dataUrl.substring(i + 'base64,'.length) : dataUrl;
  }

  private async sha256ToBase64(buffer: ArrayBuffer): Promise<string> {
    const hashBuf = await crypto.subtle.digest('SHA-256', buffer);
    let binary = '';
    const bytes = new Uint8Array(hashBuf);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  private ensureExtension(name: string, extNoDot: string): string {
    const dot = '.' + extNoDot.replace(/^\./, '');
    if (!name) return 'image' + dot;
    const idx = name.lastIndexOf('.');
    if (idx < 0) return name + dot;
    return name.substring(0, idx) + dot;
  }
}
